import React from 'react';
import { PolicyPage, Section, Callout } from './PolicyLayout';
import { GOLD,  GOLD_DIM, GOLD_MID } from './PolicyLayout';
import { CheckCircle2, XCircle, AlertCircle } from 'lucide-react';

const RELATED = [
  { label: 'Privacy Policy',      path: '/privacy'      },
  { label: 'Terms of Service',    path: '/terms'        },
  { label: 'Cookie Policy',       path: '/cookies'      },
  { label: 'Cancellation Policy', path: '/cancellation' },
];

const SECTIONS = [
  { id: 'overview',  label: 'Policy Overview'         },
  { id: 'standard',  label: 'Standard Cancellations'  },
  { id: 'dates',     label: 'Date Changes'            },
  { id: 'group',     label: 'Group Bookings'          },
  { id: 'no-show',   label: 'No-Shows & Late Arrival' },
  { id: 'provider',  label: 'We Cancel'               },
  { id: 'weather',   label: 'Weather & Force Majeure' },
  { id: 'refunds',   label: 'Refund Process'          },
  { id: 'insurance', label: 'Travel Insurance'        },
  { id: 'contact',   label: 'Contact'                 },
];

/* ── RefundTier — fully mobile-first ── */
const RefundTier = ({ window: timeWindow, refund, type, desc }) => {
  const isGreen   = type === 'full';
  const isOrange  = type === 'partial';
  const color     = isGreen ? '#22c55e' : isOrange ? GOLD : '#ef4444';
  const borderCol = isGreen ? 'rgba(34,197,94,0.25)' : isOrange ? GOLD_MID : 'rgba(239,68,68,0.2)';
  const bg        = isGreen ? 'rgba(34,197,94,0.07)'  : isOrange ? GOLD_DIM  : 'rgba(239,68,68,0.07)';
  const Icon      = isGreen ? CheckCircle2 : isOrange ? AlertCircle : XCircle;

  return (
    <div style={{
      border: `1px solid ${borderCol}`,
      background: bg,
      marginBottom: '10px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* left accent bar */}
      <div style={{
        position: 'absolute', top: 0, left: 0, width: '3px', height: '100%',
        background: color,
      }} />

      <div style={{ padding: 'clamp(14px,3vw,20px) clamp(14px,3vw,20px) clamp(14px,3vw,20px) clamp(18px,4vw,24px)' }}>
        {/* Top row: icon + time window + refund % */}
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '12px',
          marginBottom: '8px',
          flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
            <Icon size={16} strokeWidth={1.5} style={{ color, flexShrink: 0 }} />
            <span className="f-jost" style={{
              fontSize: 'clamp(14px,2.5vw,16px)', fontWeight: 400,
              color: '#12100e', lineHeight: 1.3,
            }}>
              {timeWindow}
            </span>
          </div>
          {/* Refund badge — stays right, never wraps off */}
          <div style={{
            flexShrink: 0,
            background: color,
            padding: '4px 12px',
            display: 'inline-flex', alignItems: 'center',
          }}>
            <span className="f-cormorant" style={{
              fontSize: 'clamp(22px,3vw,24px)', fontWeight: 300,
              color: '#fff', lineHeight: 1, letterSpacing: '.02em',
            }}>
              {refund}
            </span>
          </div>
        </div>

        {/* Description */}
        <p className="f-jost" style={{
          fontSize: 'clamp(14px,2vw,16px)', fontWeight: 300,
          color: '#12100e', lineHeight: 1.65, margin: 0,
          paddingLeft: '26px', /* aligns under the window text past the icon */
        }}>
          {desc}
        </p>
      </div>
    </div>
  );
};

/* ── Policy table (mobile scrollable with visible hint) ── */
const PT = ({ children }) => (
  <div style={{
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    margin: '20px 0',
    border: '1px solid #e8e4de',
    /* shadow-scroll visual hint on mobile */
    background: `
      linear-gradient(to right, white 30%, rgba(255,255,255,0)) left,
      linear-gradient(to left,  white 30%, rgba(255,255,255,0)) right,
      linear-gradient(to right, rgba(184,151,90,.18), rgba(184,151,90,0)) left,
      linear-gradient(to left,  rgba(184,151,90,.18), rgba(184,151,90,0)) right
    `,
    backgroundRepeat: 'no-repeat',
    backgroundSize: '40px 100%, 40px 100%, 14px 100%, 14px 100%',
    backgroundAttachment: 'local, local, scroll, scroll',
  }}>
    <table style={{
      width: '100%', minWidth: '540px', borderCollapse: 'collapse',
      fontFamily: "'Jost', sans-serif",
    }}>
      {children}
    </table>
  </div>
);

const TH = ({ children }) => (
  <th style={{
    fontSize: '12px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase',
    color: GOLD, padding: '10px 14px',
    background: 'rgba(184,151,90,0.12)', border: '1px solid rgba(184,151,90,0.28)',
    textAlign: 'left', whiteSpace: 'nowrap',
  }}>{children}</th>
);

const TD = ({ children, style }) => (
  <td style={{
    fontSize: '15px', fontWeight: 300, color: '#12100e',
    padding: '12px 14px', border: '1px solid #ece8e2',
    lineHeight: 1.55, verticalAlign: 'top', ...style,
  }}>{children}</td>
);

/* ════════════════════════════════════════
   PAGE
════════════════════════════════════════ */
const CancellationPolicy = () => (
  <PolicyPage
    badge="Legal · Cancellations"
    title="Cancellation Policy"
    subtitle="We know plans change. Our policy is designed to be as flexible and fair as possible. Free date changes are always available — and full refunds within 7 days of your tour."
    updated="January 15, 2026"
    heroImage="https://images.unsplash.com/photo-1519501025264-65ba15a82390?w=1400&q=80"
    sections={SECTIONS}
    relatedPages={RELATED}
    currentPage="/cancellation"
  >

    {/* ── 01 OVERVIEW ── */}
    <Section id="overview" label="The Essentials" title="Policy at a Glance">
      <p className="policy-p">
        We believe in treating our guests fairly. Travel plans change — flights get delayed, life gets in the way. This policy is our commitment to being as accommodating as possible while maintaining the viability of small-group, expert-led experiences that require careful preparation.
      </p>

      <div style={{ margin: '24px 0' }}>
        <RefundTier
          window="More than 7 days before your tour"
          refund="100%"
          type="full"
          desc="Full refund to your original payment method within 5 business days. No questions asked."
        />
        <RefundTier
          window="3 – 7 days before your tour"
          refund="50%"
          type="partial"
          desc="50% of the booking value refunded. Alternatively, transfer to any future date at no charge."
        />
        <RefundTier
          window="Less than 48 hours before your tour"
          refund="0%"
          type="none"
          desc="Within 48 hours, the booking is non-refundable. However, you may transfer your place to another person at any time."
        />
      </div>

      <Callout>
        <strong>Free date changes — always.</strong> Regardless of when you request it, changing your tour date is always free of charge, subject to availability.
      </Callout>
    </Section>

    <div className="section-divider" />

    {/* ── 02 STANDARD ── */}
    <Section id="standard" label="Standard Policy" title="Standard Cancellation Terms">
      <p className="policy-p">
        The following terms apply to all individual and small group bookings (fewer than 5 guests) unless a specific tour listing states otherwise.
      </p>
      <PT>
        <thead>
          <tr>
            <TH>Cancellation Window</TH>
            <TH>Refund</TH>
            <TH>Processing Time</TH>
            <TH>Alternative</TH>
          </tr>
        </thead>
        <tbody>
          {[
            ['More than 7 days prior',   <strong style={{color:'#22c55e'}}>100%</strong>, '5 business days', 'Free date change'],
            ['3 – 7 days prior',         <strong style={{color:GOLD}}>50%</strong>,        '5 business days', 'Free date change (no penalty)'],
            ['24 – 72 hours prior',      <strong style={{color:'#f97316'}}>25%</strong>,   '5 business days', 'Transfer booking to another person'],
            ['Less than 24 hours prior', <strong style={{color:'#ef4444'}}>0%</strong>,    'N/A',             'Transfer booking to another person'],
          ].map(([w, r, t, a], i) => (
            <tr key={i} style={{ background: i % 2 === 1 ? '#faf9f7' : '#fff' }}>
              <TD>{w}</TD><TD>{r}</TD><TD>{t}</TD><TD>{a}</TD>
            </tr>
          ))}
        </tbody>
      </PT>
      <p className="policy-p">
        All cancellation windows are calculated from the confirmed tour <em>start time</em>, not the date. A tour starting at 09:00 on Saturday — "48 hours prior" means 09:00 on Thursday.
      </p>
    </Section>

    <div className="section-divider" />

    {/* ── 03 DATES ── */}
    <Section id="dates" label="Date Changes" title="Changing Your Tour Date">
      <p className="policy-p">
        Date changes are always free — always. We understand that the best-laid plans require flexibility, and we will never penalise you for rescheduling.
      </p>
      <ul className="policy-ul">
        <li>Date changes can be made up to 6 hours before your tour start time</li>
        <li>You may change your date as many times as needed — no limit, no fees</li>
        <li>Changes are subject to availability on your preferred new date</li>
        <li>If your preferred date is unavailable, we offer the next available slot or a full refund</li>
        <li>Any price difference between original and rescheduled tour will be charged or refunded accordingly</li>
      </ul>
      <p className="policy-p">
        To change your date, email <strong>bookings@voleluxe.co.tz</strong> with your booking reference and preferred new date, or call our concierge line.
      </p>
    </Section>

    <div className="section-divider" />

    {/* ── 04 GROUP ── */}
    <Section id="group" label="Group Bookings" title="Group & Private Tour Cancellations">
      <p className="policy-p">
        Private and large group bookings (5 or more guests) are subject to modified terms, reflecting the additional preparation required for exclusive experiences.
      </p>
      <PT>
        <thead>
          <tr>
            <TH>Cancellation Window</TH>
            <TH>Refund</TH>
            <TH>Notes</TH>
          </tr>
        </thead>
        <tbody>
          {[
            ['More than 14 days prior',    <strong style={{color:'#22c55e'}}>100%</strong>, 'Full refund'],
            ['7 – 14 days prior',          <strong style={{color:GOLD}}>50%</strong>,        'Guide and venue fees are non-recoverable'],
            ['3 – 7 days prior',           <strong style={{color:'#f97316'}}>25%</strong>,   'Administrative costs apply'],
            ['Less than 72 hours prior',   <strong style={{color:'#ef4444'}}>0%</strong>,    'Non-refundable — transfer only'],
          ].map(([w, r, n], i) => (
            <tr key={i} style={{ background: i % 2 === 1 ? '#faf9f7' : '#fff' }}>
              <TD>{w}</TD><TD>{r}</TD><TD>{n}</TD>
            </tr>
          ))}
        </tbody>
      </PT>
      <p className="policy-p">
        For corporate and incentive group bookings, bespoke cancellation terms may be negotiated at the time of booking and will be specified in your group booking contract.
      </p>
    </Section>

    <div className="section-divider" />

    {/* ── 05 NO-SHOW ── */}
    <Section id="no-show" label="No-Shows" title="No-Shows & Late Arrivals">
      <p className="policy-p">
        Our tours begin promptly at the scheduled time. We operate small, intimate groups and late arrivals disrupt the experience for all guests.
      </p>
      <ul className="policy-ul">
        <li><strong>Late arrival (under 15 minutes):</strong> We will wait up to 15 minutes past the start time. Beyond this, your guide may proceed. No refund is provided.</li>
        <li><strong>No-show:</strong> If you do not arrive and have not contacted us, the booking is forfeited with no refund.</li>
        <li><strong>Running late:</strong> Call the guide number in your confirmation email — we will do our best to accommodate you.</li>
        <li><strong>Medical emergency:</strong> Contact us within 24 hours with documentation. We will offer a date change or partial credit at our discretion.</li>
      </ul>
      <Callout>
        We strongly recommend arriving 10 minutes before your tour start time. Your confirmation email includes the exact meeting point and your guide's direct contact number.
      </Callout>
    </Section>

    <div className="section-divider" />

    {/* ── 06 PROVIDER ── */}
    <Section id="provider" label="If We Cancel" title="Cancellations by Us">
      <p className="policy-p">
        We cancel tours only in exceptional circumstances. When we do, we take full responsibility and ensure you are never out of pocket.
      </p>
      <ul className="policy-ul">
        <li><strong>Full refund:</strong> If we cancel your tour for any reason within our control, you receive a 100% refund within 5 business days.</li>
        <li><strong>Advance notice:</strong> We give as much notice as possible — generally at least 48 hours, and often considerably more.</li>
        <li><strong>Alternative dates:</strong> We will offer alternative available dates before processing a refund.</li>
        <li><strong>Consequential costs:</strong> We are not liable for any consequential costs you incur (e.g. non-refundable flights or hotels). This is why we recommend travel insurance.</li>
      </ul>
    </Section>

    <div className="section-divider" />

    {/* ── 07 WEATHER ── */}
    <Section id="weather" label="Weather & Events" title="Weather & Force Majeure">
      <p className="policy-p">
        Our tours operate in all weather conditions unless conditions are deemed genuinely unsafe. We are experienced in all seasons and rarely cancel due to weather.
      </p>
      <ul className="policy-ul">
        <li><strong>Light rain:</strong> Tours proceed as normal. We provide quality umbrellas where needed.</li>
        <li><strong>Severe weather</strong> (storms, flooding, extreme cold): If conditions are unsafe, we cancel and offer a full refund or free reschedule.</li>
        <li><strong>Force majeure</strong> (natural disasters, government restrictions, civil unrest, pandemics): We will offer a full credit valid for 24 months. Refunds may be available depending on unrecoverable costs.</li>
      </ul>
      <p className="policy-p">
        Weather cancellation decisions are made by our senior operations team no later than 3 hours before tour start. You will be notified immediately by email and phone.
      </p>
    </Section>

    <div className="section-divider" />

    {/* ── 08 REFUNDS ── */}
    <Section id="refunds" label="Refund Process" title="How Refunds Are Processed">
      <p className="policy-p">
        We aim to make refunds as quick and painless as possible.
      </p>
      <ul className="policy-ul">
        <li><strong>Method:</strong> Refunds are returned to the original payment method used at booking.</li>
        <li><strong>Timeline:</strong> We process within 2 business days. Your bank may take a further 3 – 5 business days to show the credit.</li>
        <li><strong>Currency:</strong> Refunds are issued in USD. Your refund may differ slightly from the original charge due to exchange rate movements.</li>
        <li><strong>Partial refunds:</strong> Calculated as a percentage of the total booking value including any add-ons.</li>
        <li><strong>Credit vouchers:</strong> If you choose a credit voucher instead of a cash refund, we add an additional 10% in value as a thank-you for your loyalty.</li>
      </ul>
      <p className="policy-p">
        To request a cancellation and refund, email <strong>bookings@voleluxe.co.tz</strong> with your booking reference and reason. You will receive confirmation within 2 hours during business hours.
      </p>
    </Section>

    <div className="section-divider" />

    {/* ── 09 INSURANCE ── */}
    <Section id="insurance" label="Travel Insurance" title="Why We Recommend Travel Insurance">
      <p className="policy-p">
        While our cancellation policy is designed to be generous, there are scenarios that fall outside any policy's scope — personal illness closer to the tour date, travel disruptions, family emergencies.
      </p>
      <p className="policy-p">We strongly recommend comprehensive travel insurance covering:</p>
      <ul className="policy-ul">
        <li>Trip cancellation and curtailment</li>
        <li>Medical expenses and emergency evacuation</li>
        <li>Loss of personal belongings</li>
        <li>Travel delay and missed connections</li>
      </ul>
      <Callout>
        Travel insurance is particularly important for guests travelling internationally, as our cancellation policy cannot compensate for non-refundable flights or accommodation. We are happy to provide documentation to support an insurance claim.
      </Callout>
    </Section>

    <div className="section-divider" />

    {/* ── 10 CONTACT ── */}
    <Section id="contact" label="Get in Touch" title="Cancellations & Questions">
      <p className="policy-p">
        Our concierge team handles all cancellation and rebooking requests with care. Please reach out as early as possible to maximise your refund entitlement.
      </p>
      <ul className="policy-ul">
        <li><strong>Email:</strong> bookings@voleluxe.co.tz</li>
        <li><strong>Phone:</strong> +1 (800) LUX-TOUR — Monday – Sunday, 08:00 – 20:00</li>
        <li><strong>Emergency line:</strong> Available 24 / 7 for same-day cancellations</li>
        <li><strong>Response time:</strong> Within 2 hours during business hours; within 4 hours outside business hours</li>
      </ul>
      <p className="policy-p">
        Please have your booking reference ready — it is in your confirmation email and begins with <strong>"VL-"</strong>.
      </p>
    </Section>

  </PolicyPage>
);

export default CancellationPolicy;